import { useActions } from "./useActions"
import { useAppStateSelector } from "./useAppStateSelector"
import { pick } from "@nosto/search-js/utils"

/**
 * @hidden
 * Preact hook that import simplified pagination to the component.
 * @example
 *
 * ```jsx
 * import { useSimplePagination } from "@nosto/preact"
 * import { Icon } from "./elements"
 *
 * export default () => {
 *  const { hasPrev, hasNext, goToPrev, goToNext } = useSimplePagination()
 *
 *     return (
 *         <ul class="ns-d-flex ns-flex-wrap ns-justify-content-center ns-list-unstyled ns-m-0 ns-p-0 ns-my-2 ns-mx-0">
 *             {hasPrev && (
 *                 <a
 *                     onClick={goToPrev}
 *                     disabled={!hasPrev}
 *                     class="ns-page-link ns-d-block ns-previous ns-relative ns-text-undecorated ns-clickable ns-py-2 ns-px-4 ns-color-black ns-border-radius-3"
 *                 >
 *                     <Icon name="arrow" />
 *                </a>
 *             )}
 *              //TO DO: use lower level element for page link
 *             {hasNext && (
 *                 <a
 *                     onClick={goToNext}
 *                     disabled={!hasNext}
 *                     class="ns-page-link ns-d-block ns-next ns-relative ns-text-undecorated ns-clickable ns-py-2 ns-px-4 ns-color-black ns-border-radius-3"
 *                 >
 *                     <Icon name="arrow" />
 *                 </a>
 *             )}
 *         </ul>
 *     )
 * }
 * ```
 * @group Hooks
 */

export function useSimplePagination(): {
    hasPrev: boolean
    hasNext: boolean
    goToPrev: () => void
    goToNext: () => void
} {
    const { query, response } = useAppStateSelector(state => pick(state, "query", "response"))
    const { updateSearch } = useActions()

    //TO DO: use hasMoreResults field from response which is not yet implemented
    const hasNextPage = true

    if (!response?.products) {
        return {
            hasPrev: false,
            hasNext: false,
            goToPrev: () => {},
            goToNext: () => {}
        }
    }

    const from = query.products?.from ?? 0
    const pageSize = response.products.size!

    const hasPrev = from > 0
    const hasNext = hasNextPage || response.products.hits.length >= pageSize

    const goToPrev = () => {
        if (hasPrev) {
            const newFrom = Math.max(0, from - pageSize)
            updateSearch({ products: { from: newFrom } })
            scrollTo(0, 0)
        }
    }

    const goToNext = () => {
        if (hasNext) {
            const newFrom = from + pageSize
            updateSearch({ products: { from: newFrom } })
            scrollTo(0, 0)
        }
    }

    return {
        hasPrev,
        hasNext,
        goToPrev,
        goToNext
    }
}
